/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, Tooltip, useTheme } from "@mui/material";
import { GridColDef, GridColumnGroup } from "@mui/x-data-grid-premium";
import { RequireNonAggregationRow } from "components/strategyDashboard/heats/RequireNonAggregationRow";
import {
  EstimatedLoadingZonePosition,
  HeatSeries,
  LoadingZoneColumn,
  MaterialRead,
  StrategyDashboardHeatData,
} from "src/store/api/generatedApi";
import { TFunction } from "i18next";

const getPositionMaterial = (
  positions: EstimatedLoadingZonePosition[],
  area: string
): string | null =>
  positions.find((p) => p.area === area)?.material_name ?? null;

const CarCell = ({
  materialName,
  materialMap,
  t,
}: {
  materialName: string | null;
  materialMap: Map<string, MaterialRead>;
  t: TFunction;
}) => {
  const theme = useTheme();
  if (!materialName) {
    return (
      <Box
        sx={{
          flex: 1,
          backgroundColor: "grey.50",
          minHeight: 14,
        }}
      />
    );
  }

  const mat = materialMap.get(materialName);
  const color = mat?.colour ?? theme.palette.grey[500];
  const short = mat?.short_name ?? materialName.slice(0, 4);

  return (
    <Tooltip title={t("materialEstimated", { material: materialName })} arrow>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: color,
          color: theme.palette.common.white,
          fontSize: "0.55rem",
          fontWeight: "fontWeightSemiBold",
          minHeight: 14,
        }}
      >
        {short}
      </Box>
    </Tooltip>
  );
};

export const createLoadingZoneColumns = (
  data: StrategyDashboardHeatData,
  materials: MaterialRead[],
  t: TFunction
): GridColDef<HeatSeries>[] => {
  const layout = data.loading_zone_layout ?? [];
  if (layout.length === 0) return [];

  const materialMap = new Map(materials.map((m) => [m.name, m]));
  const maxCars = Math.max(...layout.map((col) => col.areas.length));

  return layout.map((column: LoadingZoneColumn, index: number) => ({
    field: `loading_zone_${index}`,
    headerName: column.label,
    width: 52,
    sortable: false,
    resizable: false,
    headerAlign: "center" as const,
    align: "center" as const,
    renderCell: RequireNonAggregationRow(({ row }) => {
      const positions = row.estimated_loading_zone_positions ?? [];
      const padCount = maxCars - column.areas.length;

      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            py: "1px",
          }}
        >
          {column.areas.map((area) => (
            <CarCell
              key={area}
              materialName={getPositionMaterial(positions, area)}
              materialMap={materialMap}
              t={t}
            />
          ))}
          {Array.from({ length: padCount }, (_, i) => (
            <Box key={`pad-${i}`} sx={{ flex: 1, minHeight: 14 }} />
          ))}
        </Box>
      );
    }),
  }));
};

export const createLoadingZoneColumnGroup = (
  data: StrategyDashboardHeatData,
  t: TFunction
): GridColumnGroup => {
  const layout = data.loading_zone_layout ?? [];

  return {
    groupId: "estimatedLoadingZonePositions",
    headerName: t("estimatedLoadingZone"),
    children: layout.map((_: LoadingZoneColumn, index: number) => ({
      field: `loading_zone_${index}`,
    })),
  };
};
